import { useCallback, useEffect, useState } from 'react';

type GetFn = <T>(path: string) => Promise<T>;

export const PWA_PENDING_CHATS_BADGE_REFRESH_EVENT = 'va-pwa-pending-chats-badge-refresh';

export function refreshPendingChatsBadge(): void {
  window.dispatchEvent(new Event(PWA_PENDING_CHATS_BADGE_REFRESH_EVENT));
}

/** Conversaciones abiertas en modo humano cuyo último mensaje es del visitante. */
export function usePendingChatsBadgeCount(
  get: GetFn,
  can: (required: string | string[]) => boolean,
  permissionsLoaded: boolean,
  pollMs = 12_000
): number {
  const [count, setCount] = useState(0);
  const canRead = can('pwa.conversations.read');
  const enabled = permissionsLoaded && canRead;

  const refresh = useCallback(async () => {
    if (!enabled) {
      setCount(0);
      return;
    }
    try {
      const d = await get<{ count: number }>('/api/console/conversations/human-attention-count?pending=1');
      setCount(typeof d.count === 'number' && d.count > 0 ? d.count : 0);
    } catch {
      /* red: mantener último valor */
    }
  }, [get, enabled]);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
    const id = window.setInterval(() => void refresh(), pollMs);
    const onVis = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    const onBadgeEvent = () => void refresh();
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener(PWA_PENDING_CHATS_BADGE_REFRESH_EVENT, onBadgeEvent);
    window.addEventListener('focus', onBadgeEvent);
    window.addEventListener('pageshow', onBadgeEvent);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener(PWA_PENDING_CHATS_BADGE_REFRESH_EVENT, onBadgeEvent);
      window.removeEventListener('focus', onBadgeEvent);
      window.removeEventListener('pageshow', onBadgeEvent);
    };
  }, [refresh, enabled, pollMs]);

  return count;
}
